import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Users } from "lucide-react";
import { publicApi } from "../lib/api";
import { getIcon } from "../lib/icons";
import Seo, { breadcrumbSchema } from "../components/Seo";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

export default function LegalCashieringService() {
  const { slug } = useParams();
  const [lc, setLc] = useState(null);

  useEffect(() => { publicApi.legalCashiering().then(setLc).catch(() => {}); }, []);

  if (!lc) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;
  const svc = (lc.services || []).find((s) => s.slug === slug);
  if (!svc) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-6">
      <p className="text-slate-500">That service couldn't be found.</p>
      <Link to="/legal-cashiering" className="text-teal font-bold">← Back to Legal Cashiering</Link>
    </div>
  );

  const Icon = getIcon(svc.icon);
  const jsonLd = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Legal Cashiering", path: "/legal-cashiering" },
      { name: svc.title, path: `/legal-cashiering/${svc.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: svc.title,
      name: `${svc.title} — CASS Legal Cashiering`,
      description: svc.intro,
      areaServed: "GB",
      provider: { "@id": `${origin()}/#organization` },
      url: `${origin()}/legal-cashiering/${svc.slug}`,
    },
  ];

  return (
    <div>
      <Seo title={`${svc.title} | Legal Cashiering`} description={svc.intro} jsonLd={jsonLd} />

      <section className="numbers-pattern text-white relative">
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/legal-cashiering" data-testid="lc-service-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-teal transition-colors mb-6"><ArrowLeft size={15} /> Legal Cashiering</Link>
          <span className="w-14 h-14 rounded-2xl bg-teal text-white flex items-center justify-center mb-5"><Icon size={26} /></span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-tight max-w-3xl">{svc.title}</h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl leading-relaxed">{svc.intro}</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display font-extrabold text-2xl text-navy mb-5">What's included</h2>
            <ul className="space-y-3">
              {(svc.points || []).map((p, i) => (
                <li key={i} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" /> <span className="text-sm font-semibold text-navy">{p}</span></li>
              ))}
            </ul>
            {svc.who && (
              <div className="mt-8 bg-slate-50 rounded-xl p-5 border border-navy/5 flex items-start gap-3">
                <Users size={18} className="text-teal shrink-0 mt-0.5" />
                <p className="text-sm text-slate-500 leading-relaxed"><span className="font-bold text-navy">Who it's for: </span>{svc.who}</p>
              </div>
            )}
          </div>
          {svc.deliverables?.length > 0 && (
            <div className="bg-navy rounded-2xl p-8 text-white h-fit">
              <h3 className="font-display font-bold text-xl mb-5">What you get</h3>
              <ul className="space-y-3">
                {svc.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-200"><CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" /> <span>{d}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <section className="py-14 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Want a tailored quote?</h2>
          <p className="mt-3 text-slate-500">Tell us about your firm and we'll come back with pricing built around your caseload.</p>
          <Link to="/legal-cashiering#enquire" data-testid="lc-service-cta" className="mt-6 inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">Request a quote <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
